import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FileTextIcon, Trash2Icon, UploadIcon } from 'lucide-react';
import { Accept } from 'react-dropzone';
import { UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FileUpload from '@/components/atoms/FileUpload/FileUpload';
import { Button } from '@/components/ui/button';
import { UploadedFile } from '@/shared/types/server';
import { FIFTY_MB } from '@/util/constants';

const BYTES_PER_MB = 1_000_000;
const RHF_SETVALUE_CONFIG = { shouldDirty: true, shouldValidate: true } as const;

const getFileNameFromUrl = (url: string): string => {
  const lastSegment = url.split('?')[0].split('#')[0].split('/').pop();
  if (!lastSegment) {
    return url;
  }
  try {
    return decodeURIComponent(lastSegment);
  } catch {
    return lastSegment;
  }
};

interface MediaFieldProps {
  error: boolean;
  errorMessage?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>;
  urlValue: string;
  name: string;
  accept?: Accept | null;
  onFileRemoved?: () => void;
  onChange?: () => void;
  shouldCrop?: boolean;
  aspectRatio?: 'horizontal' | 'square';
}

const MediaField = ({
  error,
  errorMessage,
  setValue,
  urlValue,
  name: inputName,
  accept,
  onFileRemoved,
  onChange,
  shouldCrop,
  aspectRatio,
}: MediaFieldProps) => {
  const { t } = useTranslation();
  const [uploaded, setUploaded] = useState<{ url: string; name: string } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const hasFile = !!urlValue;
  const uploadedName = uploaded?.url === urlValue ? uploaded.name : null;
  const displayName = uploadedName ?? (urlValue ? getFileNameFromUrl(urlValue) : null);
  const shownError = uploadError ?? (error ? errorMessage : null);
  const acceptedExtensions = useMemo(
    () => (accept ? Object.values(accept).flat().join(', ') : ''),
    [accept]
  );

  const message = useMemo(
    () => (
      <span className="flex flex-col items-center gap-2">
        <UploadIcon size={16} strokeWidth={1.25} className="text-foreground" />
        <span className="text-sm leading-6 font-medium text-foreground">
          {t('file_upload.dnd_replace_prefix')}{' '}
          <span className="text-primary">{t('file_upload.dnd_replace_browse')}</span>
          {hasFile && <> {t('file_upload.dnd_replace_suffix')}</>}
        </span>
        {acceptedExtensions && (
          <span className="text-xs text-muted-foreground">
            {t('file_upload.supported_file_types', { 0: acceptedExtensions })}
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          {t('file_upload.max_file_size', { 0: FIFTY_MB / BYTES_PER_MB })}
        </span>
      </span>
    ),
    [t, hasFile, acceptedExtensions]
  );

  // Ref (not a dep): the Carousel's inline onChange would otherwise re-fire FileUpload's upload effect every render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const handleFileUploaded = useCallback(
    (file: UploadedFile) => {
      setUploadError(null);
      setUploaded({ url: file.url, name: file.name });
      setValue(inputName, file.url, RHF_SETVALUE_CONFIG);
      onChangeRef.current?.();
    },
    [inputName, setValue]
  );

  const handleUploadError = useCallback(() => {
    setUploadError(t('file_upload.upload_failed'));
  }, [t]);

  const handleUploadStart = useCallback(() => {
    setUploadError(null);
  }, []);

  const handleRemove = useCallback(() => {
    setUploaded(null);
    setUploadError(null);
    onFileRemoved?.();
  }, [onFileRemoved]);

  return (
    <div>
      <div className="min-h-[132px] [&>section]:m-0">
        <FileUpload
          message={message}
          onFileUploaded={handleFileUploaded}
          stream
          accept={accept ?? undefined}
          dropzoneOnly
          maxSizeInBytes={FIFTY_MB}
          shouldCrop={shouldCrop}
          aspectRatio={aspectRatio}
          onUploadError={handleUploadError}
          onUploadStart={handleUploadStart}
        />
      </div>

      {shownError && <p className="mt-1 text-sm text-warning">{shownError}</p>}

      {hasFile && displayName && (
        <div className="group mt-2 flex items-center justify-between gap-2 rounded-md px-1 py-1 hover:bg-muted/50">
          <span className="flex min-w-0 items-center gap-1 text-xs leading-4 font-medium text-muted-foreground">
            <FileTextIcon size={16} className="shrink-0" />
            <span className="truncate">{displayName}</span>
          </span>
          <Button
            type="button"
            variant="ghost-neutral"
            size="icon-sm"
            onClick={handleRemove}
            aria-label={t('common.remove')}
            className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive focus-visible:opacity-100 hover:[&_svg]:text-destructive"
          >
            <Trash2Icon size={16} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default MediaField;
